import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  answerRequest, assignRequest, bindRequestAttachment, claimRequest, createRequest, fetchInbox,
  fetchMyRequests, fetchRequestDetail, fetchRequestTypes, patchRequest, postRequestComment,
  type CreateRequestPayload, type RequestStatus,
} from "@/api/internalRequests";

function invalidate(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ["myRequests"] });
  qc.invalidateQueries({ queryKey: ["requestInbox"] });
  qc.invalidateQueries({ queryKey: ["homeSummary"] });
  qc.invalidateQueries({ queryKey: ["requestDetail"] });
}

/** #9 S2 — participant-gated detail (request + thread + history + attachments). */
export function useRequestDetail(id: string | null) {
  return useQuery({
    queryKey: ["requestDetail", id],
    queryFn: ({ signal }) => fetchRequestDetail(id!, signal),
    enabled: !!id,
    staleTime: 15_000,
  });
}

export function useBindRequestAttachment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, fileId }: { id: string; fileId: string }) => bindRequestAttachment(id, fileId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["requestDetail"] }),
  });
}

/** #9 S4 — thread comment. */
export function useCommentRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: string }) => postRequestComment(id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["requestDetail"] }),
  });
}

/** #9 S4 — requester answer (needs_info → in_progress, SLA resume). */
export function useAnswerRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: string }) => answerRequest(id, body),
    onSuccess: () => invalidate(qc),
  });
}

export function useRequestTypes(enabled = true) {
  return useQuery({ queryKey: ["requestTypes"], queryFn: ({ signal }) => fetchRequestTypes(signal), enabled, staleTime: 5 * 60_000 });
}
export function useMyRequests(enabled = true) {
  return useQuery({ queryKey: ["myRequests"], queryFn: ({ signal }) => fetchMyRequests(signal), enabled, staleTime: 30_000 });
}
export function useRequestInbox(enabled = true) {
  return useQuery({ queryKey: ["requestInbox"], queryFn: ({ signal }) => fetchInbox(signal), enabled, staleTime: 30_000 });
}
export function useCreateRequest() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (p: CreateRequestPayload) => createRequest(p), onSuccess: () => invalidate(qc) });
}
export function useClaimRequest() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (id: string) => claimRequest(id), onSuccess: () => invalidate(qc) });
}
export function useAssignRequest() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: ({ id, userId }: { id: string; userId: string }) => assignRequest(id, userId), onSuccess: () => invalidate(qc) });
}
export function usePatchRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: {
      id: string; status: RequestStatus; result?: string; rejectReason?: string;
      resultStatus?: string; followUpTask?: { title?: string; dueDate?: string }; question?: string;
    }) => patchRequest(id, body),
    onSuccess: () => invalidate(qc),
  });
}
